import { Input } from './Input';
import { tUi } from '../i18n';
import {
  WEEKDAYS,
  type OperatingHours,
  isDayOpen,
  setDayClosed,
  setDayOpen,
  updateDayHours,
  formatDayHours,
} from '../utils/operatingHours';

export interface OperatingHoursEditorProps {
  value: OperatingHours;
  onChange: (hours: OperatingHours) => void;
  isEditing: boolean;
}

export function OperatingHoursEditor({
  value,
  onChange,
  isEditing,
}: OperatingHoursEditorProps) {
  const toggleClosed = (dayKey: string, closed: boolean) => {
    if (closed) {
      onChange(setDayClosed(value, dayKey));
    } else {
      onChange(setDayOpen(value, dayKey));
    }
  };

  return (
    <div className="space-y-1">
      {WEEKDAYS.map((day) => {
        const open = isDayOpen(value, day.key);
        const hours = value[day.key];

        return (
          <div
            key={day.key}
            className={`flex flex-col gap-2 py-3 border-b border-gray-100 last:border-0 sm:flex-row sm:items-center sm:justify-between ${
              !open && isEditing ? 'opacity-80' : ''
            }`}
          >
            <span className="font-medium text-gray-900 w-24 shrink-0">{day.label}</span>

            {isEditing ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:flex-1">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none sm:mr-2">
                  <input
                    type="checkbox"
                    checked={!open}
                    onChange={(e) => toggleClosed(day.key, e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  {tUi('closed')}
                </label>

                {open ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      className="w-32"
                      value={hours?.open ?? '08:00'}
                      onChange={(e) =>
                        onChange(updateDayHours(value, day.key, 'open', e.target.value))
                      }
                    />
                    <span className="text-gray-500 text-sm">{tUi('until')}</span>
                    <Input
                      type="time"
                      className="w-32"
                      value={hours?.close ?? '17:00'}
                      onChange={(e) =>
                        onChange(updateDayHours(value, day.key, 'close', e.target.value))
                      }
                    />
                  </div>
                ) : (
                  <span className="text-sm text-gray-500 sm:min-w-[17rem] sm:text-right">
                    {tUi('noBookingThisDay')}
                  </span>
                )}
              </div>
            ) : (
              <span
                className={`text-sm sm:text-right ${
                  open ? 'text-gray-600' : 'text-gray-400 italic'
                }`}
              >
                {formatDayHours(value, day.key)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
